import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import unset from 'lodash/fp/unset';
import {
  getStartRoundForFase,
  isRoundAtOrAfter,
  isRoundInWildcardScope,
} from '@utils/fase';

const matchFields = [
  'teamAway',
  'teamHome',
  'scoreAway',
  'scoreHome',
  'winner'
];

const areEqual = (source, target) => {
  let changes = pick(source, matchFields);
  return isEqual(changes, pick(target, Object.keys(changes)));
};

/** undefined en el payload = borrar el campo; se persiste como null en el estado. */
export const normalizeMatchChanges = (payload = {}) =>
  Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      value === undefined ? null : value,
    ])
  );

const syncBracketFromResults = (state, payload = {}) => {
  const { matches = [], faseActivacion, hasRoundOf32 = true } = payload;
  const startRound = faseActivacion
    ? getStartRoundForFase(faseActivacion, hasRoundOf32)
    : null;

  // El SYNC sólo toca matches AT/AFTER la fase de activación y dentro del
  // scope del comodín (excluye la fase bloqueada = final).
  const bracketFromResults = matches.filter(
    (match) =>
      match &&
      match.stage === 'bracket' &&
      typeof match.mid === 'number' &&
      (!startRound || isRoundInWildcardScope(match.round, startRound))
  );

  const synced = bracketFromResults.map((match) => {
    const isActivationRound = startRound && match.round === startRound;

    if (isActivationRound) {
      return {
        mid: match.mid,
        teamHome: match.teamHome,
        teamAway: match.teamAway,
        scoreHome: undefined,
        scoreAway: undefined,
        winner: undefined,
        dirty: true,
      };
    }

    // Fases posteriores: borrar pronóstico previo; los equipos se propagan al cargar scores
    return { mid: match.mid, dirty: true };
  });

  const syncedMids = new Set(synced.map((row) => row.mid));
  const matchRoundByMid = matches.reduce(
    (all, match) =>
      match && typeof match.mid === 'number'
        ? { ...all, [match.mid]: match.round }
        : all,
    {}
  );

  const untouched = state
    .filter((row) => {
      if (syncedMids.has(row.mid)) return false;
      const round = matchRoundByMid[row.mid];
      if (!round) return true;
      if (startRound && isRoundInWildcardScope(round, startRound)) return false;
      return true;
    })
    .map((row) => {
      const round = matchRoundByMid[row.mid];
      if (
        startRound &&
        round &&
        row &&
        row.modificadoPorComodin &&
        !isRoundAtOrAfter(round, startRound)
      ) {
        const { modificadoPorComodin, ...rest } = row;
        return rest;
      }
      return row;
    });

  return [...untouched, ...synced];
};

export default (state = [], action = {}) => {
  switch (action.type) {
    case 'FORECAST_MATCHES_SET':
      return action.payload;
    case 'FORECAST_MATCHES_RESET_DIRTY':
      return state.map(unset('dirty'));
    case 'FORECAST_MATCHES_MATCH_SET': {
      const changes = normalizeMatchChanges(action.payload);
      let exists = state.some(m => m.mid === action.meta.mid);
      return exists
        ? state.map(
            match =>
              match.mid === action.meta.mid && !areEqual(changes, match)
                ? { ...match, ...changes, dirty: true }
                : match
          )
        : [...state, { mid: action.meta.mid, ...changes, dirty: true }];
    }
    case 'FORECAST_MATCHES_SYNC_BRACKET_FROM_RESULTS':
      return syncBracketFromResults(state, action.payload);
    default:
      return state;
  }
};
